import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ScribbleUnderline } from "@/components/Icon";

interface PageHeaderProps {
  title: string;
  /** Mono-uppercase eyebrow над заголовком. */
  eyebrow?: string;
  /** Editorial-заголовок з 2 частин (як Feed/Quests/Subscription):
   *  titleLead — звичайний чорний текст (перший рядок),
   *  titleAccent — accent-italic помаранчевий зі scribble (другий рядок).
   *  Якщо задані обидва — рендеримо editorial-hero. */
  titleLead?: string;
  titleAccent?: string;
  /** @deprecated ширина підкреслення тепер міряється по самому слову;
   *  значення лишається лише як запас до першого заміру. */
  scribbleWidth?: number;
  subtitle?: string;
  /** Кнопки праворуч від eyebrow (пошук, оновити) — як у Стрічці. */
  actions?: ReactNode;
}

/**
 * Page header.
 * - З `titleLead` + `titleAccent` → Editorial-hero 1:1 з Feed/Quests/
 *   Subscription/Referrals: eyebrow + чорний lead + br + accent-italic
 *   зі scribble. Юзер 2026-06-06.
 * - Інакше → старий простий заголовок (Admin, Categories).
 */
/** Кеглі, з яких добираємо найбільший, при якому заголовок лишається в ОДИН
 *  рядок. 2.125rem — базовий, 1.625rem — підлога (як у Рефералах). */
const HERO_STEPS = [2.125, 2, 1.875, 1.75, 1.625];

export function PageHeader({
  title,
  eyebrow,
  titleLead,
  titleAccent,
  scribbleWidth = 160,
  subtitle,
  actions,
}: PageHeaderProps) {
  // Заголовок в один рядок (owner 2026-08-24: «Виконуй заробляй надо в один
  // ряд, как в рефералке»). Довгі заголовки не переносимо, а зменшуємо кегль
  // до підлоги; якщо навіть вона не влазить — дозволяємо перенос, щоб текст
  // не обрізався. Міряємо після завантаження шрифту: Unbounded вантажиться
  // асинхронно, до нього ширина тексту інша.
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const [heroRem, setHeroRem] = useState(HERO_STEPS[0]);
  const [heroWrap, setHeroWrap] = useState(false);
  // Підкреслення міряємо по САМОМУ слову (owner 2026-09-11: «підчеркування
  // довше ніж треба»). Раніше ширина була магічним числом на кожній сторінці
  // і роз'їхалась, коли кегль заголовка почав підбиратись автоматично.
  const accentRef = useRef<HTMLSpanElement>(null);
  const [accentW, setAccentW] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = accentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setAccentW(Math.round(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [titleAccent, heroRem]);

  useLayoutEffect(() => {
    if (!titleLead || !titleAccent) return;
    let cancelled = false;
    const fit = () => {
      const el = h1Ref.current;
      if (!el || cancelled) return;
      const prev = el.style.whiteSpace;
      el.style.whiteSpace = "nowrap";
      let picked = HERO_STEPS[HERO_STEPS.length - 1];
      let fits = false;
      // На час заміру ховаємо scribble: він лежить absolute і вилазить за
      // текст, тому і scrollWidth, і Range-прямокутник виходили ширші за
      // контейнер — підбір завжди скочувався в підлогу.
      const scribble = el.querySelector("svg");
      const scribblePrev = scribble ? scribble.getAttribute("style") : null;
      if (scribble) scribble.setAttribute("style", "display:none");
      for (const rem of HERO_STEPS) {
        el.style.fontSize = `${rem}rem`;
        if (el.scrollWidth <= el.clientWidth) {
          picked = rem;
          fits = true;
          break;
        }
      }
      if (scribble) {
        if (scribblePrev === null) scribble.removeAttribute("style");
        else scribble.setAttribute("style", scribblePrev);
      }
      el.style.whiteSpace = prev;
      // ⚠️ НЕ чистити inline font-size: якщо підібраний крок збігся з тим, що
      // вже у стейті, React не перемалює <h1> — і заголовок лишиться на
      // успадкованих 16px (саме так він і «зсихався» при першій спробі).
      el.style.fontSize = `${picked}rem`;
      if (cancelled) return;
      setHeroRem(picked);
      setHeroWrap(!fits);
    };
    fit();
    const fonts = (document as any).fonts;
    if (fonts?.ready) fonts.ready.then(fit).catch(() => {});
    window.addEventListener("resize", fit);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", fit);
    };
  }, [titleLead, titleAccent]);

  const heroScale = heroRem / HERO_STEPS[0];
  // Відступ під шапкою залежить від того, чи є в ній опис розділу:
  //  • є subtitle → pb-6: саме цю відстань «опис → контент» просив збільшити
  //    owner 2026-08-23 («дуже маленька відстань»);
  //  • немає subtitle → pb-2: наступний блок сторінки має власний відступ
  //    зверху, і два великих поспіль дають кілометр пустоти (owner 2026-08-24,
  //    Стрічка / Квести / База знань).
  if (titleLead && titleAccent) {
    return (
      <header className={`relative pt-4 ${subtitle ? "pb-6" : "pb-2"}`}>
        {(eyebrow || actions) && (
          <div className="flex items-center justify-between gap-2">
            {eyebrow ? (
              <div className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {eyebrow}
              </div>
            ) : (
              <span />
            )}
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        )}
        {/* ⚠️ ЄДИНИЙ РОЗМІР ЗАГОЛОВКА ДЛЯ ВСІХ СТОРІНОК (owner 2026-08-11:
            «що я, ебаний хаос, чому правила дизайну не захардкоджені?»).
            До цього кожна сторінка мала свій кегль: Стрічка 32, Квести 38,
            Підписка 34, Реферали 26 — і відстань від логотипа до тексту скрізь
            була різна. Тут одне значення, міняти лише тут.
            Перенос рядка НЕ форсуємо: короткий заголовок лишається в один
            рядок (як у Стрічці), довгий переноситься сам. */}
        <h1
          ref={h1Ref}
          className="mt-3 font-display font-bold text-ink"
          style={{
            fontSize: `${heroRem}rem`,
            whiteSpace: heroWrap ? "normal" : "nowrap",
            lineHeight: 1.02,
            letterSpacing: "-0.025em",
            // Компенсація left side bearing Unbounded — заголовок штрихом
            // по лівому краю (точці eyebrow), не зсунутий праворуч.
            marginLeft: "-0.04em",
          }}
        >
          {titleLead}{" "}
          <span className="relative inline-block">
            <span
              ref={accentRef}
              className="accent-italic"
              style={{ fontWeight: 600 }}
            >
              {titleAccent}
            </span>
            <ScribbleUnderline
              width={accentW ?? Math.round(scribbleWidth * heroScale)}
              stretch={accentW !== null}
              className="absolute -bottom-2 -left-1"
            />
          </span>
        </h1>
        {subtitle && (
          <p className="mt-4 text-[0.875rem] leading-relaxed text-ink-dim [text-wrap:balance]">
            {subtitle}
          </p>
        )}
      </header>
    );
  }

  return (
    // Простий варіант: той самий відступ зверху, що й у hero — інакше
    // заголовок стрибає між сторінками (owner 2026-09-12).
    <header className={`pt-4 ${subtitle ? "pb-6" : "pb-2"}`}>
      <h1
        className="font-display font-bold tracking-tight text-ink"
        style={{ fontSize: "1.75rem", lineHeight: 1.05, letterSpacing: "-0.02em" }}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="mt-2 text-sm leading-relaxed text-ink-dim [text-wrap:balance]">
          {subtitle}
        </p>
      )}
    </header>
  );
}
